/**
 * Item relationship flags — what an item carries with it wherever it is shown
 * in a ranked or decided list, and what is checked where rankings meet.
 *
 * Canon 12 Rule 17 (owner ruling 2026-10-12): a relationship that reaches
 * outside the room is flagged, never disallowed. The flag travels with the
 * item; a split is settled at the lowest level that contains every member.
 *
 *   - Package / hard dependency / whole-and-parts / duplicates reach across
 *     Tracks, so they are compared where the Tracks meet (the Cycle).
 *   - Competing members are alike, so they sit in one Track and the choice is
 *     made there.
 *
 * Pure: no store, no network. Callers pass the workspace relationship graph.
 */

import type {
  ItemRelationshipEdge,
  ItemRelationshipGroup,
  ItemRelationshipGroupMembership,
} from '../types/prioritization/itemRelationships';

export type RelationshipFlagKind = 'package' | 'competing' | 'needs' | 'whole' | 'part';

export interface ItemRelationshipFlag {
  kind: RelationshipFlagKind;
  /** relationshipGuide id — icon and colour come from there. */
  guideId: 'package' | 'exclusive' | 'dependency' | 'composition';
  /** Short words for the chip. */
  label: string;
  /** The other items in the relationship. */
  otherItemIds: string[];
}

export interface RelationshipGraph {
  groups: Pick<ItemRelationshipGroup, 'id' | 'type' | 'status' | 'title'>[];
  memberships: Pick<ItemRelationshipGroupMembership, 'groupId' | 'itemId'>[];
  edges: Pick<ItemRelationshipEdge, 'type' | 'subtype' | 'status' | 'sourceItemId' | 'targetItemId'>[];
}

const liveGroup = (g: { status?: string | null }) => g.status !== 'dissolved' && g.status !== 'dismissed';
const liveEdge = (e: { status?: string | null }) => e.status !== 'dissolved' && e.status !== 'dismissed';

function groupMembers(graph: RelationshipGraph, type: string, opts: { candidateOnly?: boolean } = {}): string[][] {
  const out: string[][] = [];
  for (const g of graph.groups) {
    if (g.type !== type || !liveGroup(g)) continue;
    if (opts.candidateOnly && g.status !== 'candidate') continue;
    const members = [...new Set(graph.memberships.filter((m) => m.groupId === g.id).map((m) => m.itemId))];
    if (members.length >= 2) out.push(members);
  }
  return out;
}

/** Hard requirements as [dependent, required] pairs, from either assertion direction. */
function hardRequirements(graph: RelationshipGraph): Array<[string, string]> {
  const out: Array<[string, string]> = [];
  for (const e of graph.edges) {
    if (!liveEdge(e)) continue;
    if (e.subtype !== 'hard' && e.subtype !== 'gate') continue;
    if (e.type === 'dependency') out.push([e.sourceItemId, e.targetItemId]);
    if (e.type === 'prerequisite') out.push([e.targetItemId, e.sourceItemId]);
  }
  return out;
}

/** Whole-and-parts as [whole, part] pairs. */
function compositions(graph: RelationshipGraph): Array<[string, string]> {
  return graph.edges
    .filter((e) => liveEdge(e) && (e.type === 'aggregation' || e.type === 'breakdown'))
    .map((e) => [e.sourceItemId, e.targetItemId]);
}

/** The flags one item carries. Empty when it has no relationship that needs telling. */
export function describeItemRelationshipFlags(itemId: string, graph: RelationshipGraph): ItemRelationshipFlag[] {
  const flags: ItemRelationshipFlag[] = [];
  const others = (lists: string[][]) => [...new Set(lists.filter((m) => m.includes(itemId)).flat())].filter((id) => id !== itemId);

  const packageOthers = others(groupMembers(graph, 'package'));
  if (packageOthers.length > 0) flags.push({ kind: 'package', guideId: 'package', label: 'Package', otherItemIds: packageOthers });

  const competingOthers = others(groupMembers(graph, 'exclusive'));
  if (competingOthers.length > 0) flags.push({ kind: 'competing', guideId: 'exclusive', label: 'Competing', otherItemIds: competingOthers });

  const needs = [...new Set(hardRequirements(graph).filter(([d]) => d === itemId).map(([, r]) => r))];
  if (needs.length > 0) flags.push({ kind: 'needs', guideId: 'dependency', label: 'Needs', otherItemIds: needs });

  const parts = [...new Set(compositions(graph).filter(([w]) => w === itemId).map(([, p]) => p))];
  if (parts.length > 0) flags.push({ kind: 'whole', guideId: 'composition', label: 'Has parts', otherItemIds: parts });

  const wholes = [...new Set(compositions(graph).filter(([, p]) => p === itemId).map(([w]) => w))];
  if (wholes.length > 0) flags.push({ kind: 'part', guideId: 'composition', label: 'Part of', otherItemIds: wholes });

  return flags;
}

/** The sentence shown when someone points at a flag. */
export function relationshipFlagSentence(flag: ItemRelationshipFlag, titleOf: (id: string) => string): string {
  const names = flag.otherItemIds.map(titleOf).join(', ');
  switch (flag.kind) {
    case 'package':
      return `Part of a package with: ${names}. It only works if all of them are chosen.`;
    case 'competing':
      return `Competes with: ${names}. Only one of them can be chosen.`;
    case 'needs':
      return `Needs: ${names}. It cannot be done unless that is chosen too.`;
    case 'whole':
      return `The whole of: ${names}. Ranking the whole and its parts together counts the work twice.`;
    case 'part':
      return `A part of: ${names}. Ranking the whole and its parts together counts the work twice.`;
  }
}

// ============================================================================
// Where rankings meet
// ============================================================================

/** One ranking — a Track's result, or a session's. */
export interface RankedResult {
  id: string;
  name: string;
  /** Ordered, best first. */
  rankedItemIds: string[];
  /** Items before this index are chosen. null/undefined = no line drawn, so every ranked item counts as chosen. */
  cutLinePosition?: number | null;
}

const chosenIds = (r: RankedResult): string[] =>
  r.cutLinePosition == null ? r.rankedItemIds : r.rankedItemIds.slice(0, Math.max(0, r.cutLinePosition));

/**
 * Competing members that were both chosen in one ranking. Competing items are
 * alike, so they are ranked against each other in one Track — the choice
 * between them is made there, not higher up.
 *
 * Ranking competing items against each other is the expected state, so with no
 * line drawn there is no choice yet and nothing to find.
 */
export function findCompetingBothChosen(result: RankedResult, graph: RelationshipGraph): string[][] {
  if (result.cutLinePosition == null) return [];
  const chosen = new Set(chosenIds(result));
  return groupMembers(graph, 'exclusive')
    .map((members) => members.filter((id) => chosen.has(id)))
    .filter((both) => both.length >= 2);
}

export type MeetingFindingKind =
  | 'package_split'
  | 'need_not_chosen'
  | 'whole_and_parts'
  | 'duplicate_across';

export interface MeetingFinding {
  kind: MeetingFindingKind;
  /** Items the finding is about, chosen ones first. */
  itemIds: string[];
  /** Chosen somewhere. */
  chosenItemIds: string[];
  /** Ranked somewhere, but below the line. */
  notChosenItemIds: string[];
  /** In no ranking at all. */
  unrankedItemIds: string[];
  /** Rankings that hold any of the items — where the finding goes back down to. */
  resultIds: string[];
}

/**
 * Compare several rankings where they meet (the Tracks of a Cycle) and return
 * every relationship whose members' results disagree. Nothing is repaired: each
 * finding names the items and the rankings to send it back down to.
 */
export function findSplitsWhereRankingsMeet(results: RankedResult[], graph: RelationshipGraph): MeetingFinding[] {
  const chosen = new Set<string>();
  const ranked = new Set<string>();
  const holders = new Map<string, Set<string>>();
  for (const r of results) {
    for (const id of r.rankedItemIds) {
      ranked.add(id);
      (holders.get(id) ?? holders.set(id, new Set()).get(id)!).add(r.id);
    }
    for (const id of chosenIds(r)) chosen.add(id);
  }

  const build = (kind: MeetingFindingKind, ids: string[]): MeetingFinding => {
    const chosenItemIds = ids.filter((id) => chosen.has(id));
    const notChosenItemIds = ids.filter((id) => ranked.has(id) && !chosen.has(id));
    const unrankedItemIds = ids.filter((id) => !ranked.has(id));
    const resultIds = [...new Set(ids.flatMap((id) => [...(holders.get(id) ?? [])]))];
    return { kind, itemIds: [...chosenItemIds, ...notChosenItemIds, ...unrankedItemIds], chosenItemIds, notChosenItemIds, unrankedItemIds, resultIds };
  };

  const findings: MeetingFinding[] = [];

  // Package: some members chosen, others not.
  for (const members of groupMembers(graph, 'package')) {
    const f = build('package_split', members);
    if (f.chosenItemIds.length > 0 && f.chosenItemIds.length < members.length) findings.push(f);
  }

  // Hard need: the dependent was chosen, what it needs was not.
  for (const [dependent, required] of hardRequirements(graph)) {
    if (chosen.has(dependent) && !chosen.has(required)) findings.push(build('need_not_chosen', [dependent, required]));
  }

  // Whole and parts: the whole and one of its parts were both chosen.
  const partsByWhole = new Map<string, string[]>();
  for (const [whole, part] of compositions(graph)) {
    if (chosen.has(whole) && chosen.has(part)) (partsByWhole.get(whole) ?? partsByWhole.set(whole, []).get(whole)!).push(part);
  }
  for (const [whole, parts] of partsByWhole) findings.push(build('whole_and_parts', [whole, ...new Set(parts)]));

  // Unresolved duplicate ranked in more than one place.
  for (const members of groupMembers(graph, 'cluster', { candidateOnly: true })) {
    const present = members.filter((id) => ranked.has(id));
    if (present.length < 2) continue;
    const f = build('duplicate_across', present);
    if (f.resultIds.length >= 2) findings.push(f);
  }

  return findings;
}

/** Plain words for a finding. */
export function meetingFindingSentence(f: MeetingFinding, titleOf: (id: string) => string): { title: string; detail: string } {
  const list = (ids: string[]) => ids.map(titleOf).join(', ');
  switch (f.kind) {
    case 'package_split': {
      const missing = [
        f.notChosenItemIds.length > 0 ? `not chosen: ${list(f.notChosenItemIds)}` : '',
        f.unrankedItemIds.length > 0 ? `not ranked anywhere: ${list(f.unrankedItemIds)}` : '',
      ].filter(Boolean).join('; ');
      return { title: 'A package was split', detail: `Chosen: ${list(f.chosenItemIds)}. The rest of the package — ${missing}. It only works if all of them are chosen.` };
    }
    case 'need_not_chosen': {
      const [dependent, required] = [f.chosenItemIds[0], [...f.notChosenItemIds, ...f.unrankedItemIds][0]];
      const why = f.unrankedItemIds.length > 0 ? 'has not been ranked anywhere' : 'was not chosen';
      return { title: 'Chosen without what it needs', detail: `${titleOf(dependent)} was chosen. It needs ${titleOf(required)}, which ${why}.` };
    }
    case 'whole_and_parts':
      return { title: 'The whole and its parts were both chosen', detail: `${titleOf(f.itemIds[0])} was chosen, and so were its parts: ${list(f.itemIds.slice(1))}. The work is counted twice.` };
    case 'duplicate_across':
      return { title: 'The same thing was ranked in two places', detail: `${list(f.itemIds)} may be the same item under different names. Decide whether they are before comparing the results.` };
  }
}
